import { z } from 'zod';
import { ToolOptions, ResourceOptions } from './types.js';

/**
 * Metadata for tool decorators
 */
export interface ToolMetadata {
  id: string;
  options: ToolOptions;
  handler: (args: any, context: any) => Promise<any>;
  className?: string;
  methodName?: string;
  filePath?: string;
  lineNumber?: number;
}

/**
 * Metadata for resource decorators
 */
export interface ResourceMetadata {
  id: string;
  options: ResourceOptions;
  handler: (context: any) => Promise<any>;
  className?: string;
  methodName?: string;
  filePath?: string;
  lineNumber?: number;
}

/**
 * LLM-friendly schema representation
 */
export interface LLMSchema {
  type: 'object' | 'array' | 'string' | 'number' | 'boolean';
  description?: string;
  properties?: Record<string, LLMSchema>;
  items?: LLMSchema;
  required?: string[];
  enum?: any[];
  format?: string;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  examples?: any[];
}

/**
 * LLM-friendly tool representation
 */
export interface LLMTool {
  name: string;
  description: string;
  inputSchema: LLMSchema;
  examples?: Array<{
    input: any;
    output: any;
    description?: string;
  }>;
  deprecated?: boolean;
  metadata?: Record<string, unknown>;
}

/**
 * LLM-friendly resource representation
 */
export interface LLMResource {
  name: string;
  description: string;
  mimeType?: string;
  examples?: Array<{
    output: any;
    description?: string;
  }>;
  deprecated?: boolean;
  metadata?: Record<string, unknown>;
}

/**
 * Metadata registry for managing decorator metadata
 */
export class MetadataRegistry {
  private tools = new Map<string, ToolMetadata>();
  private resources = new Map<string, ResourceMetadata>();

  /**
   * Register tool metadata
   */
  registerTool(metadata: ToolMetadata): void {
    if (this.tools.has(metadata.id)) {
      throw new Error(`Tool with id '${metadata.id}' is already registered`);
    }
    this.tools.set(metadata.id, metadata);
  }

  /**
   * Register resource metadata
   */
  registerResource(metadata: ResourceMetadata): void {
    if (this.resources.has(metadata.id)) {
      throw new Error(`Resource with id '${metadata.id}' is already registered`);
    }
    this.resources.set(metadata.id, metadata);
  }

  /**
   * Get tool metadata
   */
  getTool(id: string): ToolMetadata | undefined {
    return this.tools.get(id);
  }

  /**
   * Get resource metadata
   */
  getResource(id: string): ResourceMetadata | undefined {
    return this.resources.get(id);
  }

  /**
   * Get all tool metadata
   */
  getAllTools(): ToolMetadata[] {
    return Array.from(this.tools.values());
  }

  /**
   * Get all resource metadata
   */
  getAllResources(): ResourceMetadata[] {
    return Array.from(this.resources.values());
  }

  /**
   * Convert zod schema to LLM-friendly format
   */
  static zodToLLMSchema(schema: z.ZodSchema): LLMSchema {
    if (schema instanceof z.ZodObject) {
      const shape = schema.shape;
      const properties: Record<string, LLMSchema> = {};
      const required: string[] = [];

      for (const [key, value] of Object.entries(shape)) {
        properties[key] = this.zodToLLMSchema(value as z.ZodSchema);
        
        // Check if field is required
        if (!(value instanceof z.ZodOptional)) {
          required.push(key);
        }
      }

      return {
        type: 'object',
        properties,
        required: required.length > 0 ? required : undefined
      };
    }

    if (schema instanceof z.ZodArray) {
      return {
        type: 'array',
        items: this.zodToLLMSchema(schema.element)
      };
    }

    if (schema instanceof z.ZodString) {
      const llmSchema: LLMSchema = {
        type: 'string',
        description: schema.description
      };

      if (schema._def.checks) {
        for (const check of schema._def.checks) {
          if (check.kind === 'regex') {
            llmSchema.pattern = check.regex.source;
          }
        }
      }

      return llmSchema;
    }

    if (schema instanceof z.ZodNumber) {
      const llmSchema: LLMSchema = {
        type: 'number',
        description: schema.description
      };

      if (schema._def.checks) {
        for (const check of schema._def.checks) {
          if (check.kind === 'min') {
            llmSchema.minimum = check.value;
          } else if (check.kind === 'max') {
            llmSchema.maximum = check.value;
          }
        }
      }

      return llmSchema;
    }

    if (schema instanceof z.ZodBoolean) {
      return {
        type: 'boolean',
        description: schema.description
      };
    }

    if (schema instanceof z.ZodEnum) {
      return {
        type: 'string',
        enum: schema._def.values,
        description: schema.description
      };
    }

    if (schema instanceof z.ZodUnion) {
      // For unions, we'll use the first option as the primary type
      return this.zodToLLMSchema(schema._def.options[0]);
    }

    if (schema instanceof z.ZodOptional) {
      return this.zodToLLMSchema(schema.unwrap());
    }

    // Default fallback
    return {
      type: 'string',
      description: schema.description
    };
  }

  /**
   * Generate LLM-friendly tool representation
   */
  generateLLMTool(toolId: string): LLMTool | null {
    const tool = this.getTool(toolId);
    if (!tool) return null;

    const inputSchema = MetadataRegistry.zodToLLMSchema(tool.options.input);

    return {
      name: tool.options.name,
      description: tool.options.description,
      inputSchema,
      deprecated: tool.options.deprecated,
      metadata: tool.options.metadata
    };
  }

  /**
   * Generate LLM-friendly resource representation
   */
  generateLLMResource(resourceId: string): LLMResource | null {
    const resource = this.getResource(resourceId);
    if (!resource) return null;

    return {
      name: resource.options.name,
      description: resource.options.description,
      mimeType: resource.options.mimeType,
      deprecated: resource.options.deprecated,
      metadata: resource.options.metadata
    };
  }

  /**
   * Generate complete LLM schema for all tools and resources
   */
  generateCompleteLLMSchema(): {
    tools: LLMTool[];
    resources: LLMResource[];
  } {
    const tools = this.getAllTools().map(tool => 
      this.generateLLMTool(tool.id)
    ).filter(Boolean) as LLMTool[];

    const resources = this.getAllResources().map(resource => 
      this.generateLLMResource(resource.id)
    ).filter(Boolean) as LLMResource[];

    return { tools, resources };
  }

  /**
   * Export metadata as JSON for external tools
   */
  exportMetadata(): string {
    const schema = this.generateCompleteLLMSchema();
    return JSON.stringify(schema, null, 2);
  }

  /**
   * Clear all metadata
   */
  clear(): void {
    this.tools.clear();
    this.resources.clear();
  }
} 